import { buildAssessmentSectionDetailedName } from '@app/src/pages/TopicalRisk/Drawer/topicalRiskDrawerUtils'
import type Assessment from '@app/src/types/assessment'
import { RiskStatus } from '@app/src/types/resourceExplorer'
import type { SustainabilityTopicRisk } from '@app/src/types/topicalRisk'

export interface AssessmentRow {
  assessmentTemplateId?: number
  name: string
  level?: number
  levelsCount?: number
  isActivated: boolean
  isExcludedDueToLowData: boolean
}

/**
 * The two assessments a topic risk can draw on: the topic's own template (first priority)
 * and the section of a broader template it belongs to (second priority).
 */
export function getAssessmentRows(
  risk: SustainabilityTopicRisk,
  activatedAssessmentTemplateIds: Set<number>,
): AssessmentRow[] {
  const rows: AssessmentRow[] = []
  const levelsCount = risk.assessmentTemplateLevels

  if (risk.assessmentTemplateId > 0 && risk.assessmentTemplate) {
    rows.push({
      assessmentTemplateId: risk.assessmentTemplateId,
      name: risk.assessmentTemplate.name,
      level: risk.assessmentLevel,
      levelsCount,
      isActivated: activatedAssessmentTemplateIds.has(risk.assessmentTemplateId),
      isExcludedDueToLowData: Boolean(risk.isAssessmentExcludedDueToLowData),
    })
  }

  const section = risk.assessmentTemplateSection
  if (risk.assessmentTemplateSectionId > 0 && section?.assessmentTemplate) {
    rows.push({
      assessmentTemplateId: section.assessmentTemplateId,
      name:
        buildAssessmentSectionDetailedName(section.assessmentTemplate.name, section.name) ??
        section.assessmentTemplate.name,
      level: risk.assessmentSectionLevel,
      levelsCount,
      isActivated: Boolean(
        section.assessmentTemplateId && activatedAssessmentTemplateIds.has(section.assessmentTemplateId),
      ),
      isExcludedDueToLowData: Boolean(risk.isAssessmentSectionExcludedDueToLowData),
    })
  }

  return rows
}

/**
 * Whether the assessment has actually been answered. An activated assessment nobody has
 * responded to yet has no result page worth opening.
 */
export function hasAssessmentData(row: AssessmentRow): boolean {
  return Boolean(row.level || row.isExcludedDueToLowData)
}

/**
 * The row whose level actually fed the risk calculation, mirroring the backend's priority cascade.
 * Must run against the rows in backend order, before any display reordering.
 */
export function getEffectiveAssessmentRow(rows: AssessmentRow[]): AssessmentRow | undefined {
  return rows.find(row => row.level && !row.isExcludedDueToLowData)
}

/**
 * Display order: the assessment that produced the result first, then other activated ones,
 * then the ones still to be activated. Ties keep the backend's template-before-section order.
 */
export function orderAssessmentRowsForDisplay(rows: AssessmentRow[], effectiveRow?: AssessmentRow): AssessmentRow[] {
  const rank = (row: AssessmentRow) => {
    if (row === effectiveRow) return 0
    return row.isActivated ? 1 : 2
  }

  return [...rows].sort((left, right) => rank(left) - rank(right))
}

/**
 * Whether an assessment result actually adjusted the inherent risk, mirroring the backend's
 * priority cascade. A level excluded for low data (only on older stored topic risks) is still
 * reported on the topic risk but never reached the risk matrix.
 */
export function hasEffectiveAssessmentResult(risk: SustainabilityTopicRisk): boolean {
  return Boolean(
    (risk.assessmentLevel && !risk.isAssessmentExcludedDueToLowData) ||
    (risk.assessmentSectionLevel && !risk.isAssessmentSectionExcludedDueToLowData),
  )
}

const ACTUAL_RISK_LEVELS = [RiskStatus.Low, RiskStatus.Medium, RiskStatus.High, RiskStatus.Extreme]

/**
 * An AI-screened result is capped at inherent risk, so a topic risk equal to it may have been capped.
 * The stored risk does not say whether the cap applied, so this also holds when the matrix happened to
 * land on inherent risk.
 */
export function mayBeCappedAtInherentRisk(risk: SustainabilityTopicRisk, effectiveAssessment?: Assessment): boolean {
  return (
    Boolean(effectiveAssessment?.isScreened) &&
    risk.inherentRiskStatus !== undefined &&
    ACTUAL_RISK_LEVELS.includes(risk.inherentRiskStatus) &&
    risk.totalRiskStatus === risk.inherentRiskStatus
  )
}

/**
 * Matches a row to the provider's assessment so the row can show when and how it was reported.
 * A provider can hold one assessment per reporting period for the same template, so the most
 * recently updated one is used.
 */
export function findAssessmentForRow(row: AssessmentRow, assessments: Assessment[]): Assessment | undefined {
  const updatedAtTime = (assessment: Assessment) => new Date(assessment.updatedAt).getTime()

  return assessments
    .filter(assessment => assessment.assessmentTemplateId === row.assessmentTemplateId)
    .reduce<
      Assessment | undefined
    >((latest, assessment) => (!latest || updatedAtTime(assessment) > updatedAtTime(latest) ? assessment : latest), undefined)
}

/**
 * "Baseline: Environmental" plus the reporting period, when we know it.
 */
export function buildRowName(row: AssessmentRow, assessment?: Assessment): string {
  return assessment?.periodName ? `${row.name} (${assessment.periodName})` : row.name
}

/**
 * True when a higher-priority assessment exists for the topic but has not been activated,
 * which is what the description offers as a further deep-dive.
 */
export function hasDeeperAssessmentAvailable(rows: AssessmentRow[], effectiveRow?: AssessmentRow): boolean {
  return rows.some(row => row !== effectiveRow && !row.isActivated)
}
